import { SxProps, Theme, useMediaQuery, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { S3GRole } from '../../generated-types/authRoles';
import useSharedAuthLib from '../../lib/useSharedAuthLib';
import AuthGuard from '../AuthGuard/AuthGuard';
import NavButton from '../NavButton/NavButton';

/** The properties for the nav buttons */
interface NavButtonsProps {
  /**
   * (Optional) Set to true to show the nav buttons with the privacy policy and terms of use navigation. False by default.
   */
  withPrivacyAndTerms?: boolean;
}

/**
 * All the navigation buttons that are shown in either the header or footer
 */
const NavButtons = ({ withPrivacyAndTerms = false }: NavButtonsProps) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const authLib = useSharedAuthLib();
  const navStyle = useMemo<SxProps<Theme>>(
    () => ({
      fontSize: isMd ? '1em' : '2em',
    }),
    [isMd]
  );

  return (
    <>
      <NavButton
        id="articles-nav-button"
        to="/articles"
        component={NavLink}
        sx={navStyle}
      >
        Articles
      </NavButton>
      <NavButton
        id="about-nav-button"
        to="/about"
        component={NavLink}
        sx={navStyle}
      >
        About
      </NavButton>
      <NavButton
        id="contact-nav-button"
        to="/contact"
        component={NavLink}
        sx={navStyle}
      >
        Contact Us
      </NavButton>
      {!authLib.AuthState.Authenticated && (
        <NavButton
          id="purchase-training-nav-button"
          to="/purchase-training"
          component={NavLink}
          sx={navStyle}
        >
          Purchase Training
        </NavButton>
      )}
      <AuthGuard requiredRoles={[S3GRole.Admin]}>
        <NavButton to="/admin" component={NavLink} sx={navStyle}>
          Admin
        </NavButton>
      </AuthGuard>
      <AuthGuard requiredRoles={[S3GRole.Manager]}>
        <NavButton to="/management" component={NavLink} sx={navStyle}>
          Management
        </NavButton>
      </AuthGuard>
      <AuthGuard requiredRoles={[S3GRole.User]}>
        <NavButton to="/training" component={NavLink} sx={navStyle}>
          Training
        </NavButton>
      </AuthGuard>
      <AuthGuard requiredRoles={[S3GRole.ClientOnboarding]}>
        <NavButton to="/client-onboarding" component={NavLink} sx={navStyle}>
          Client Onboarding
        </NavButton>
      </AuthGuard>
      <AuthGuard onlyNoRoles>
        <NavButton to="/welcome" component={NavLink} sx={navStyle}>
          Welcome
        </NavButton>
      </AuthGuard>
      {withPrivacyAndTerms && (
        <>
          <NavButton
            id="privacy-policy-nav-button"
            to="/privacy-policy"
            component={NavLink}
            sx={navStyle}
          >
            Privacy Policy
          </NavButton>
          <NavButton
            id="terms-of-use-nav-button"
            to="/terms-of-use"
            component={NavLink}
            sx={navStyle}
          >
            Terms of Use
          </NavButton>
        </>
      )}
    </>
  );
};

export default NavButtons;
